import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Bell, ShieldAlert, Play, Loader2, Volume2, Radio } from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { BillingSection } from "@/components/nadia/BillingSection";
import { AdAccounts } from "@/components/nadia/AdAccounts";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { getAdConnections } from "@/lib/api";
import {
  getSettings, updateAutonomy, getMetaTracking,
  updateLeadPrefs, testAdapter, getConversionAdapters, testCapiEvent,
} from "@/lib/api";

const CHANNEL_LABELS = {
  meta_ads: "Meta Ads",
  instagram: "Instagram",
  facebook: "Facebook",
  whatsapp: "WhatsApp",
  email: "Email",
  analytics: "Analytics",
  landing_page: "Landing Page",
};

const LEVEL_DESC = {
  CONSIGLIA: "Nadia solo consiglia. Tu decidi sempre.",
  PREPARA: "Nadia prepara l'azione e aspetta la tua approvazione.",
  ESEGUE: "Nadia esegue autonomamente azioni a basso rischio.",
};

export default function Impostazioni() {
  const [s, setS] = useState(null);
  const [tracking, setTracking] = useState(null);
  const [adConnections, setAdConnections] = useState(null);
  const trackingActive = adConnections?.meta?.status === "connected" && !!tracking?.pixel?.connected;
  const [adapters, setAdapters] = useState([]);
  const [testing, setTesting] = useState(null);
  const [capiTesting, setCapiTesting] = useState(false);
  const [prefs, setPrefs] = useState({ owner_whatsapp: "", notify_owner_by_whatsapp: false, browser_push_enabled: false, voice_enabled: false });

  const loadTracking = () => getMetaTracking().then(setTracking).catch(() => setTracking(null));

  useEffect(() => {
    getSettings().then((d) => {
      setS(d);
      setPrefs({
        owner_whatsapp: d?.lead_prefs?.owner_whatsapp || "",
        notify_owner_by_whatsapp: d?.lead_prefs?.notify_owner_by_whatsapp || false,
        browser_push_enabled: d?.lead_prefs?.browser_push_enabled || false,
        voice_enabled: d?.lead_prefs?.voice_enabled || false,
      });
    }).catch(() => setS(null));
    loadTracking();
    getAdConnections().then(setAdConnections).catch(() => setAdConnections(null));
    getConversionAdapters().then((d) => setAdapters(d.adapters || [])).catch(() => setAdapters([]));
  }, []);

  const runCapiTest = async () => {
    setCapiTesting(true);
    try {
      const r = await testCapiEvent("Lead");
      toast.success(r.mock ? "Meta non configurato: nessun evento inviato" : (r.ok ? "Evento ricevuto da Meta" : "Errore CAPI"));
      loadTracking();
    } catch { toast.error("Errore"); }
    finally { setCapiTesting(false); }
  };

  const savePrefs = async (patch) => {
    const next = { ...prefs, ...patch };
    setPrefs(next);
    await updateLeadPrefs(next).catch(() => {});
  };

  const askPush = async () => {
    if (!("Notification" in window)) return toast.error("Il browser non supporta le notifiche");
    const p = await Notification.requestPermission();
    if (p === "granted") {
      savePrefs({ browser_push_enabled: true });
      toast.success("Notifiche browser attivate");
    } else {
      toast.error("Permesso rifiutato");
    }
  };

  const runAdapterTest = async (id) => {
    setTesting(id);
    try {
      const r = await testAdapter(id);
      toast.success(r.ok
        ? (r.mock ? `Servizio ${id} non configurato: test non inviato` : `Test consegnato a ${id}`)
        : `Errore su ${id}: ${r.error || "n/a"}`);
    } catch { toast.error("Errore"); }
    finally { setTesting(null); }
  };

  const change = async (channel, level) => {
    try {
      const res = await updateAutonomy(channel, level);
      setS(res);
      toast.success(`Autonomia ${CHANNEL_LABELS[channel]}: ${level}`);
    } catch {
      toast.error("Errore");
    }
  };

  if (!s) return <Skeleton className="h-96 bg-white/5" />;

  return (
    <>
      <PageHeader
        eyebrow="Impostazioni"
        title="Livelli di autonomia"
        subtitle="Per ogni canale scegli quanto Nadia può agire da sola. Le azioni ad alto rischio richiedono sempre la tua conferma."
      />

      <div className="space-y-3">
        {Object.entries(s.autonomy || {}).map(([channel, level]) => (
          <div
            key={channel}
            className="nadia-card p-5 flex flex-col sm:flex-row sm:items-center gap-4 sm:justify-between"
            data-testid={`autonomy-row-${channel}`}
          >
            <div className="min-w-0">
              <div className="font-heading text-base font-semibold text-slate-100">
                {CHANNEL_LABELS[channel] || channel}
              </div>
              <div className="text-xs text-slate-400 mt-0.5">{LEVEL_DESC[level]}</div>
            </div>
            <Select value={level} onValueChange={(v) => change(channel, v)}>
              <SelectTrigger
                className="w-full sm:w-48 bg-[#0B1120] border-white/10 text-slate-100"
                data-testid={`autonomy-select-${channel}`}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#131C2E] border-white/10 text-slate-100">
                <SelectItem value="CONSIGLIA">CONSIGLIA</SelectItem>
                <SelectItem value="PREPARA">PREPARA</SelectItem>
                <SelectItem value="ESEGUE">ESEGUE</SelectItem>
              </SelectContent>
            </Select>
          </div>
        ))}
      </div>

      <div className="nadia-card p-5 mt-6" data-testid="budget-cap-card">
        <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-2">
          Tetto di spesa mensile
        </div>
        <div className="font-heading text-3xl font-bold text-slate-50">€{s.monthly_budget_cap}</div>
        <div className="text-xs text-slate-500 mt-1">
          Nadia non supererà mai questo tetto senza la tua approvazione esplicita.
        </div>
      </div>

      <div className="mt-8 mb-3 flex items-center gap-2">
        <h2 className="font-heading text-lg font-semibold text-slate-100">Account pubblicitari</h2>
      </div>
      <p className="text-xs text-slate-400 mb-3">Collega Meta, TikTok e Google con il login ufficiale del provider. ChatGPT Ads usa una chiave API custodita sul server. Nadia pubblica solo dopo la tua approvazione esplicita.</p>
      <AdAccounts />

      <div className="mt-8 mb-3 flex items-center gap-2">
        <h2 className="font-heading text-lg font-semibold text-slate-100">Abbonamento, pagamenti e fatture</h2>
      </div>
      <BillingSection />

      {/* Lead notifications */}
      <div className="mt-8 mb-3 flex items-center gap-2">
        <h2 className="font-heading text-lg font-semibold text-slate-100">Notifiche nuovi lead</h2>
        <Bell size={14} className="text-cyan-300" />
      </div>
      <div className="nadia-card p-5 space-y-4" data-testid="lead-prefs">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-sm font-semibold text-slate-100">Notifiche in Nadia</div>
            <div className="text-xs text-slate-400">Sempre attive: appaiono nella campanella in alto.</div>
          </div>
          <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-emerald-500/10 text-emerald-300 border border-emerald-500/20">Attive</span>
        </div>
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-sm font-semibold text-slate-100">Notifica browser (push)</div>
            <div className="text-xs text-slate-400">Nadia ti avvisa anche quando l'app non è in primo piano.</div>
          </div>
          {prefs.browser_push_enabled ? (
            <Switch checked onCheckedChange={(v) => savePrefs({ browser_push_enabled: v })} data-testid="push-toggle" />
          ) : (
            <Button size="sm" variant="outline" onClick={askPush} className="border-white/10 text-slate-200 bg-transparent hover:text-cyan-200 hover:border-cyan-400/50" data-testid="push-enable">
              Attiva
            </Button>
          )}
        </div>
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-100"><Volume2 size={13} className="text-cyan-300" /> Nadia legge il lead ad alta voce</div>
            <div className="text-xs text-slate-400">Solo con l'app aperta e solo se attivi tu questa opzione. Mai in automatico senza il tuo consenso.</div>
          </div>
          <Switch
            checked={prefs.voice_enabled}
            onCheckedChange={(v) => { savePrefs({ voice_enabled: v }); toast.success(v ? "Voce attiva: Nadia leggerà i nuovi lead" : "Voce disattivata"); }}
            data-testid="voice-toggle"
          />
        </div>
        <div className="flex items-start justify-between gap-4 pt-1 border-t border-white/5">
          <div className="flex-1">
            <div className="text-sm font-semibold text-slate-100">WhatsApp al titolare</div>
            <div className="text-xs text-slate-400 mb-2">Solo il numero autorizzato riceve i lead. Nessun messaggio automatico ai clienti.</div>
            <Input
              value={prefs.owner_whatsapp}
              onChange={(e) => setPrefs({ ...prefs, owner_whatsapp: e.target.value })}
              onBlur={() => savePrefs({ owner_whatsapp: prefs.owner_whatsapp })}
              placeholder="+39 340 1234567"
              className="max-w-xs bg-[#0B1120] border-white/10 h-9 text-slate-100"
              data-testid="owner-whatsapp"
            />
          </div>
          <Switch
            checked={prefs.notify_owner_by_whatsapp}
            onCheckedChange={(v) => savePrefs({ notify_owner_by_whatsapp: v })}
            data-testid="whatsapp-toggle"
          />
        </div>
      </div>

      {/* Tracking status (Pixel/CAPI/Consent) */}
      <div className="mt-8 mb-3 flex items-center gap-2">
        <h2 className="font-heading text-lg font-semibold text-slate-100">Tracking &amp; consenso</h2>
        <ShieldAlert size={14} className="text-amber-300" />
      </div>
      <div className={`nadia-card p-5 flex items-center gap-2 ${trackingActive ? "text-emerald-300" : "text-amber-300"}`} data-testid="tracking-status" role="status">
        {trackingActive ? <CheckCircle2 size={16} /> : <XCircle size={16} />}
        <span className="text-sm font-semibold">{trackingActive ? "Tracking attivo" : "Tracking da completare"}</span>
      </div>
      <div hidden data-testid="tracking-technical-details">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="tracking-cards">
        <TrackingRow label="Meta Pixel" v={tracking?.pixel} hint={tracking?.pixel?.pixel_id_masked ? `ID ${tracking.pixel.pixel_id_masked}` : "Nessun Pixel collegato"} />
        <TrackingRow label="Conversions API (CAPI)" v={tracking?.capi} hint={tracking?.capi?.note} />
        <TrackingRow label="Consent Mode" v={tracking?.consent_mode} hint={tracking?.consent_mode?.hint} />
      </div>

      <div className="nadia-card p-5 mt-3" data-testid="capi-card">
        <div className="flex items-start gap-4 flex-wrap">
          <span className="w-10 h-10 rounded-xl bg-violet-500/10 border border-violet-500/25 text-violet-300 flex items-center justify-center shrink-0">
            <Radio size={17} />
          </span>
          <div className="flex-1 min-w-[220px]">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="text-sm font-semibold text-slate-100">Eventi server-side (Pixel live / CAPI)</div>
              <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider border ${
                tracking?.capi?.mode === "live" ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/20" : "bg-amber-500/10 text-amber-300 border-amber-500/20"
              }`} data-testid="capi-mode">{tracking?.capi?.mode === "live" ? "Connesso" : "Integrazione pronta · in attesa credenziali"}</span>
            </div>
            <p className="text-xs text-slate-400 mt-1">
              L'integrazione è pronta: ogni nuovo lead genera un evento <span className="font-mono text-slate-300">Lead</span> con dati cifrati (SHA-256).
              La connessione reale avverrà dal backend Nadia Cloud: nessuna chiave nel browser, il Pixel di produzione non viene toccato.
            </p>
            <div className="text-[11px] text-slate-500 mt-2">
              Eventi registrati: <span className="text-slate-300 font-semibold" data-testid="capi-events-total">{tracking?.events_total ?? 0}</span>
              {tracking?.recent_events?.[0] && <> · ultimo: {tracking.recent_events[0].event_name} ({tracking.recent_events[0].mode})</>}
            </div>
          </div>
          <Button
            onClick={runCapiTest}
            disabled={capiTesting}
            variant="outline"
            size="sm"
            className="border-white/10 text-slate-200 hover:text-cyan-200 hover:border-cyan-400/50 bg-transparent"
            data-testid="capi-test-button"
          >
            {capiTesting ? <Loader2 size={12} className="mr-1.5 animate-spin" /> : <Play size={12} className="mr-1.5" />} Invia evento di test
          </Button>
        </div>
      </div>
      </div>

      {/* Adapter test */}
      <div hidden>
      <div className="mt-8 mb-3 flex items-center gap-2">
        <h2 className="font-heading text-lg font-semibold text-slate-100">Test adapter conversioni</h2>
      </div>
      </div>
      {[false, true].map((advanced) => {
        const Section = advanced ? "details" : "div";
        return (
          <Section key={String(advanced)} className="mt-3" data-testid={advanced ? "advanced-integrations" : "contact-integrations"}>
            {advanced && <summary className="cursor-pointer rounded-xl border border-white/10 px-4 py-3 text-sm font-semibold text-slate-100 hover:text-cyan-200 transition-colors" data-testid="advanced-integrations-toggle">Integrazioni avanzate</summary>}
      <div className={advanced ? "space-y-2 mt-3" : "space-y-2"} data-testid={advanced ? "adapter-test-list" : "contact-adapter-list"}>
        {adapters.filter((a) => advanced !== ["whatsapp_link", "tel_link"].includes(a.id)).map((a) => (
          <div key={a.id} className="nadia-card p-4 flex items-center justify-between gap-3" data-testid={`adapter-${a.id}`}>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-slate-100">{a.label}</div>
              <div className="text-[10px] text-slate-500 font-mono mt-0.5 truncate">
                {a.connected ? "Collegato" : a.env_required?.length ? "Configurazione necessaria" : "Attivo"}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider border ${
                a.mode === "live" ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/20"
                  : a.mode === "native" ? "bg-cyan-500/10 text-cyan-300 border-cyan-500/25"
                  : "bg-amber-500/10 text-amber-300 border-amber-500/20"
              }`}>{a.mode}</span>
              <Button
                onClick={() => runAdapterTest(a.id)}
                disabled={testing === a.id}
                variant="outline"
                size="sm"
                className="border-white/10 text-slate-200 hover:text-cyan-200 hover:border-cyan-400/50 bg-transparent"
                data-testid={`adapter-test-${a.id}`}
              >
                {testing === a.id ? <Loader2 size={12} className="mr-1.5 animate-spin" /> : <Play size={12} className="mr-1.5" />} Test
              </Button>
            </div>
          </div>
        ))}
      </div>
          </Section>
        );
      })}
    </>
  );
}

function TrackingRow({ label, v, hint }) {
  const ok = !!v?.connected;
  return (
    <div className={`p-4 rounded-xl border ${ok ? "border-emerald-500/25 bg-emerald-500/[0.04]" : "border-amber-500/25 bg-amber-500/[0.04]"}`}>
      <div className="flex items-center gap-2">
        {ok ? <CheckCircle2 size={14} className="text-emerald-400" /> : <XCircle size={14} className="text-amber-400" />}
        <span className="text-sm font-semibold text-slate-100">{label}</span>
      </div>
      <div className="text-[10px] text-slate-500 mt-1 font-mono truncate">
        {v?.connected ? "Collegato" : "Configurazione necessaria"}
      </div>
      {hint && <div className="text-[11px] text-slate-500 mt-1">{hint}</div>}
    </div>
  );
}

